import ColorSwatchButton from '@/components/ColorButton/ColorSwatchButton'
import ColorPickerButton from '@/components/ColorButton/ColorPickerButton'
import type { ColorPickerPopoverPlacement } from '@/components/ColorButton/ColorPickerButton'
import SwipeRow from '@/components/SwipeRow/SwipeRow'
import InspectorGrid, { GridSize } from '@/components/InspectorGrid/InspectorGrid'

export type { ColorPickerPopoverPlacement }

export interface ColorPaletteGridProps {
  /** Flat list `string[]` renders as a single wrapping row.
   *  Nested `string[][]` renders each inner array as its own row. */
  colors: string[] | string[][]
  selectedColor: string | null
  onChange: (color: string, isFinal: boolean) => void
  showPickerButton?: boolean
  pickerConfig?: {
    placement?: ColorPickerPopoverPlacement
    offset?: { x?: number; y?: number }
  }
  isSecondaryStyle?: boolean
  /** 'swipe' puts every color on one horizontally scrolling line (phones). */
  layout?: 'wrap' | 'swipe' | 'fluid-grid'
}

function isNestedArray(c: string[] | string[][]): c is string[][] {
  return c.length > 0 && Array.isArray(c[0])
}

function ColorPaletteGrid({
  colors,
  selectedColor,
  onChange,
  showPickerButton = false,
  pickerConfig,
  isSecondaryStyle,
  layout = 'wrap',
}: ColorPaletteGridProps) {
  const rows: string[][] = isNestedArray(colors) ? colors : [colors]

  if (layout === 'fluid-grid') {
    return (
      <InspectorGrid gridSize={GridSize.Grid6}>
        {rows.flat().map((color, index) => (
          <ColorSwatchButton
            key={`${color}-${index}`}
            color={color}
            isActive={selectedColor === color}
            onClick={c => onChange(c, true)}
            fluid
          />
        ))}
        {showPickerButton && (
          <ColorPickerButton
            colorSelected={selectedColor && selectedColor !== 'transparent' ? selectedColor : '#000000'}
            onChange={onChange}
            popoverPlacement={pickerConfig?.placement}
            popoverOffset={pickerConfig?.offset}
            compact
            fluid
          />
        )}
      </InspectorGrid>
    )
  }

  if (layout === 'swipe') {
    return (
      // The picker sits outside the scrolling colors, so scrolling never hides it.
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
        {showPickerButton && (
          <ColorPickerButton
            colorSelected={selectedColor && selectedColor !== 'transparent' ? selectedColor : '#000000'}
            onChange={onChange}
            isSecondaryStyle={isSecondaryStyle}
            popoverPlacement={pickerConfig?.placement}
            popoverOffset={pickerConfig?.offset}
            compact
          />
        )}
        <SwipeRow $style={{ flex: '1 1 0', minWidth: 0, width: 'auto' }}>
          {rows.flat().map((color, index) => (
            <ColorSwatchButton
              key={`${color}-${index}`}
              color={color}
              isActive={selectedColor === color}
              onClick={c => onChange(c, true)}
              isSecondaryStyle={isSecondaryStyle}
            />
          ))}
        </SwipeRow>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {row.map((color, colIndex) => (
            <ColorSwatchButton
              key={`${color}-${colIndex}`}
              color={color}
              isActive={selectedColor === color}
              onClick={c => onChange(c, true)}
              isSecondaryStyle={isSecondaryStyle}
            />
          ))}
          {showPickerButton && rowIndex === rows.length - 1 && (
            <ColorPickerButton
              colorSelected={selectedColor && selectedColor !== 'transparent' ? selectedColor : '#000000'}
              onChange={onChange}
              isSecondaryStyle={isSecondaryStyle}
              popoverPlacement={pickerConfig?.placement}
              popoverOffset={pickerConfig?.offset}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default ColorPaletteGrid
